import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Table as BTable } from 'react-bootstrap';
import { LoadingIcon } from '../../../assets/Icon';
import TableFilters from './TableFilters';
import TableHeader from './TableHeader';
import { FilterValue, SortDirection, TableProps, TableState } from './types';

const Table: React.FC<TableProps> = ({
  title,
  columns,
  data,
  loading = false,
  onStateChange,
  onLoadMore,
  hasMore = false,
  initialState,
  mainClassName,
  tableClassName,
  tableTheadClassName,
  thClassName,
  tableBodyRowClassName,
  tdClassName,
  noDataText = 'No data found',
  scrollableContainerRef,
}) => {
  const [tableState, setTableState] = useState<TableState>({
    sort: initialState?.sort || { column: null, direction: null },
    filters: initialState?.filters || {},
  });

  const observer = useRef<IntersectionObserver | null>(null);
  const loadingRef = useCallback(
    (node: HTMLDivElement) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();

      const root = scrollableContainerRef?.current || null;
      observer.current = new IntersectionObserver(
        entries => {
          if (entries[0].isIntersecting && hasMore) {
            onLoadMore?.();
          }
        },
        { root },
      );

      if (node) observer.current.observe(node);
    },
    [loading, hasMore, onLoadMore, scrollableContainerRef],
  );

  useEffect(() => {
    if (initialState) {
      setTableState(prev => ({
        ...prev,
        ...initialState,
      }));
    }
  }, [initialState]);

  const handleSort = (column: string) => {
    const newState = { ...tableState };
    let direction: SortDirection = 'asc';

    if (tableState.sort.column === column) {
      if (tableState.sort.direction === 'asc') direction = 'desc';
      else if (tableState.sort.direction === 'desc') direction = null;
    }

    newState.sort = {
      column: direction ? column : null,
      direction,
    };

    setTableState(newState);
    onStateChange?.(newState);
  };

  const handleFilter = (filters: Record<string, FilterValue>) => {
    const newState = {
      ...tableState,
      filters,
    };
    setTableState(newState);
    onStateChange?.(newState);
  };

  const hasFilters = columns.some(column => column.filterable);

  const sortedData = React.useMemo(() => {
    if (!tableState.sort.column || !tableState.sort.direction) {
      return data;
    }
    const sorted = [...data];
    const { column, direction } = tableState.sort;

    return sorted.sort((a, b) => {
      const aValue = a[column];
      const bValue = b[column];

      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, tableState.sort]);

  return (
    <div className={`card border-0 px-4 py-2 ${mainClassName}`}>
      {title && (
        <div className="card-header border-bottom-0 bg-white">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">{title}</h5>
            <TableFilters
              columns={columns}
              filters={tableState.filters}
              onFiltersChange={handleFilter}
            />
          </div>
        </div>
      )}
      <div className="card-body p-0">
        <div className="table-responsive">
          <BTable
            hover
            className={`mb-0 ${tableClassName}`}
            style={{ width: '100%', tableLayout: 'auto' }}
          >
            <TableHeader
              columns={columns}
              sortColumn={tableState.sort.column}
              sortDirection={tableState.sort.direction}
              onSort={handleSort}
              tableTheadClassName={tableTheadClassName}
              thClassName={thClassName}
            />
            <tbody>
              {sortedData?.length > 0 ? (
                sortedData.map((row, index) => (
                  <tr
                    key={index}
                    className={tableBodyRowClassName}
                    style={{ display: 'table-row' }}
                  >
                    {columns.map(column => {
                      const cellContent = column.renderer ? (
                        column.renderer(row[column.key], row)
                      ) : (
                        <span>{row[column.key]}</span>
                      );

                      return (
                        <td
                          key={column.key}
                          style={{
                            width: column.width,
                            minWidth: column.width,
                            maxWidth: column.width,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: 'table-cell',
                          }}
                          className={`px-4 py-2 text-${column.align || 'start'} ${tdClassName}`}
                        >
                          {column.width ? (
                            <div
                              style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                minWidth: 0,
                              }}
                            >
                              {cellContent}
                            </div>
                          ) : (
                            cellContent
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length} className="text-center py-3">
                    {noDataText.split('\n').map((line, index) => (
                      <div key={index}>{line}</div>
                    ))}
                  </td>
                </tr>
              )}
            </tbody>
          </BTable>
        </div>
        {(loading || hasMore) && (
          <div ref={loadingRef} className="text-center py-3">
            {loading && (
              <div className="d-flex align-items-center justify-content-center gap-2">
                <LoadingIcon size={20} />
                <span>Loading...</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Table;
